/** Tableau devis. */
import { useFormatters } from "../../formats/useFormatters";
import { ListPager, usePagedRows } from "../../components/ListPager";
import { SortTh, sortRows, useTableSort } from "../../components/tableSort";
import "../clients/tables.css";
import { gcIsOpen } from "../../payments/gocardless";
import type { GatewayProvider } from "../../payments/gateways";
import { invoiceRemainingCents, invoiceSoldeText, invoiceSoldeTone } from "../payments/types";
import { StatusChip } from "../../status/StatusChip";
import { quoteStatus } from "./format";
import { QuotesActions } from "./QuotesActions";
import { quoteValidUntil, type Quote } from "./types";

type QuotesTableProps = {
  kind?: "quote" | "invoice" | "credit_note";
  rows: Quote[];
  emptyText?: string;
  menuId: number | null;
  onMenu: (id: number | null) => void;
  onOpen: (id: number) => void;
  onOpenClient?: (clientId: number) => void;
  onCloneQuote: (id: number) => void;
  onCloneInvoice: (id: number) => void;
  onCloneRecurring?: (id: number) => void;
  onPay?: (row: Quote) => void;
  linked?: ReadonlySet<GatewayProvider>;
  onCollect?: (row: Quote) => void;
  onGcRefresh?: (row: Quote) => void;
  onSend: (row: Quote) => void;
  onPdf: (row: Quote) => void;
  onPrint: (row: Quote) => void;
  onArchive?: (row: Quote) => void;
  onTrace: (row: Quote) => void;
  onConvert?: (row: Quote) => void;
  onCredit?: (row: Quote) => void;
  onOpenCredit?: (id: number) => void;
  onDelete: (row: Quote) => void;
};

export function QuotesTable({
  kind = "quote",
  rows,
  emptyText = "Aucun devis.",
  menuId,
  onMenu,
  onOpen,
  onOpenClient,
  onCloneQuote,
  onCloneInvoice,
  onCloneRecurring,
  onPay,
  linked,
  onCollect,
  onGcRefresh,
  onSend,
  onPdf,
  onPrint,
  onArchive,
  onTrace,
  onConvert,
  onCredit,
  onOpenCredit,
  onDelete,
}: QuotesTableProps) {
  const { money, day } = useFormatters();
  const sort = useTableSort<"status" | "number" | "client" | "date" | "due" | "ttc" | "balance">();
  const shown = sort.key
    ? sortRows(rows, sort.dir, sort.key === "date" || sort.key === "due" ? "date" : sort.key === "ttc" || sort.key === "balance" ? "num" : "text", (q) => {
        if (sort.key === "status") return quoteStatus(q);
        if (sort.key === "number") return q.number || "";
        if (sort.key === "client") return q.client_name || "";
        if (sort.key === "date") return q.issue_date || "";
        if (sort.key === "due") return quoteValidUntil(q) || "";
        if (sort.key === "ttc") return q.total_ttc_cents;
        return invoiceRemainingCents(q) ?? null;
      })
    : rows;
  const paged = usePagedRows(shown);
  if (rows.length === 0) {
    return (
      <div className="fx-clients-wrap">
        <p className="fx-clients-empty">{emptyText}</p>
      </div>
    );
  }
  return (
    <div className="fx-clients-wrap">
      <table className="fx-clients-table">
        <thead>
          <tr>
            <SortTh label="Statut" active={sort.key === "status"} dir={sort.dir} onSort={() => sort.onSort("status", "text")} />
            <SortTh label="N°" active={sort.key === "number"} dir={sort.dir} onSort={() => sort.onSort("number", "text")} />
            <SortTh label="Client" active={sort.key === "client"} dir={sort.dir} onSort={() => sort.onSort("client", "text")} />
            <SortTh label="Date" active={sort.key === "date"} dir={sort.dir} onSort={() => sort.onSort("date", "date")} />
            <SortTh
              label={kind === "quote" ? "Validité" : "Échéance"}
              active={sort.key === "due"}
              dir={sort.dir}
              onSort={() => sort.onSort("due", "date")}
            />
            <SortTh className="is-num" label="TTC" active={sort.key === "ttc"} dir={sort.dir} onSort={() => sort.onSort("ttc", "num")} />
            {kind === "invoice" ? (
              <SortTh className="is-num" label="Solde" active={sort.key === "balance"} dir={sort.dir} onSort={() => sort.onSort("balance", "num")} />
            ) : null}
            <th className="is-act" scope="col">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {paged.slice.map((q) => (
            <tr
              key={q.id}
              className={`fx-clients-table__row${q.status === "draft" ? "" : ""}`}
              onClick={() => onOpen(q.id)}
            >
              <td>
                <StatusChip row={q} />
              </td>
              <td>
                <button
                  type="button"
                  className="fx-clients-table__link"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpen(q.id);
                  }}
                >
                  {q.number || "—"}
                </button>
              </td>
              <td>
                {onOpenClient && q.client_id ? (
                  <button
                    type="button"
                    className="fx-clients-table__name fx-clients-table__link"
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenClient(q.client_id);
                    }}
                  >
                    {q.client_name || "—"}
                  </button>
                ) : (
                  <span className="fx-clients-table__name">{q.client_name || "—"}</span>
                )}
              </td>
              <td className="fx-clients-table__muted">{day(q.issue_date)}</td>
              <td className="fx-clients-table__muted">{day(quoteValidUntil(q))}</td>
              <td className="is-num">{money(q.total_ttc_cents)}</td>
              {kind === "invoice" ? (
                <td className={["is-num", invoiceSoldeTone(invoiceRemainingCents(q))].filter(Boolean).join(" ")}>
                  {invoiceSoldeText(invoiceRemainingCents(q), money)}
                </td>
              ) : null}
              <td className="is-act" onClick={(e) => e.stopPropagation()}>
                <QuotesActions
                  quote={q}
                  open={menuId === q.id}
                  onToggle={() => onMenu(menuId === q.id ? null : q.id)}
                  onClose={() => onMenu(null)}
                  onOpen={() => {
                    onMenu(null);
                    onOpen(q.id);
                  }}
                  onCloneQuote={() => {
                    onMenu(null);
                    onCloneQuote(q.id);
                  }}
                  onCloneInvoice={() => {
                    onMenu(null);
                    onCloneInvoice(q.id);
                  }}
                  onCloneRecurring={
                    onCloneRecurring
                      ? () => {
                          onMenu(null);
                          onCloneRecurring(q.id);
                        }
                      : undefined
                  }
                  onPay={
                    onPay && q.doc_type === "invoice" && q.status !== "draft" && !q.paid && !q.credited
                      ? () => {
                          onMenu(null);
                          onPay(q);
                        }
                      : undefined
                  }
                  linked={linked}
                  onCollect={
                    onCollect && q.doc_type === "invoice" && q.status !== "draft" && !q.paid && !q.credited && !gcIsOpen(q)
                      ? () => {
                          onMenu(null);
                          onCollect(q);
                        }
                      : undefined
                  }
                  onGcRefresh={
                    onGcRefresh && gcIsOpen(q)
                      ? () => {
                          onMenu(null);
                          onGcRefresh(q);
                        }
                      : undefined
                  }
                  onSend={() => {
                    onMenu(null);
                    onSend(q);
                  }}
                  onPdf={() => {
                    onMenu(null);
                    onPdf(q);
                  }}
                  onPrint={() => {
                    onMenu(null);
                    onPrint(q);
                  }}
                  onArchive={
                    onArchive && q.status !== "draft"
                      ? () => {
                          onMenu(null);
                          onArchive(q);
                        }
                      : undefined
                  }
                  onTrace={() => {
                    onMenu(null);
                    onTrace(q);
                  }}
                  onConvert={
                    kind === "quote" && onConvert && q.status !== "draft" && !q.converted_invoice_id && !q.refused_at && !q.refusal?.refused_at
                      ? () => {
                          onMenu(null);
                          onConvert(q);
                        }
                      : undefined
                  }
                  onCredit={
                    kind === "invoice" && onCredit && q.status !== "draft" && !q.credited
                      ? () => {
                          onMenu(null);
                          onCredit(q);
                        }
                      : undefined
                  }
                  onOpenCredit={
                    kind === "invoice" && onOpenCredit && q.credit_note_id
                      ? () => {
                          onMenu(null);
                          onOpenCredit(q.credit_note_id as number);
                        }
                      : undefined
                  }
                  onDelete={
                    q.status === "draft"
                      ? () => {
                          onMenu(null);
                          onDelete(q);
                        }
                      : undefined
                  }
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <ListPager page={paged.page} pages={paged.pages} total={paged.total} from={paged.from} to={paged.to} setPage={paged.setPage} />
    </div>
  );
}
